import { FC, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import $api from "../../../shared/api/axios";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";

const selectItemClasses = "px-3 py-2 text-[14px] hover:bg-gray-50 cursor-pointer focus:bg-gray-50 focus:outline-none";

type ImportRowError = { row: number; email: string | null; reason: string };
type ImportResult = { created: { email: string }[]; errors: ImportRowError[]; createdCount: number; errorCount: number };

const emptySingleUser = { email: "", firstName: "", lastName: "", company: "", password: "" };

export const ImportUsersPage: FC = () => {
  const [orgId, setOrgId] = useState("");
  const [orgSearch, setOrgSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [singleUser, setSingleUser] = useState(emptySingleUser);
  const [createdUsers, setCreatedUsers] = useState<{ email: string }[]>([]);
  const [singleUserError, setSingleUserError] = useState<string | null>(null);

  const { data: organizationsData } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["get-organizations"],
    queryFn: async () => {
      const response = await $api.get("/organizations/system-admin");
      return response?.data?.data || [];
    }
  });

  const filteredOrganizations =
    organizationsData?.filter((org) => org.name.toLowerCase().includes(orgSearch.toLowerCase())) || [];
  const selectedOrg = organizationsData?.find((org) => org.id.toString() === orgId);

  const importMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("file", file as File);
      const response = await $api.post(`/organizations/${orgId}/users/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data as { data: ImportResult; message: string };
    },
    onSuccess: (data) => setResult(data.data),
    onError: (e) => {
      const msg = (e as AxiosError<{ message: string }>).response?.data?.message || "Error importing users";
      alert(msg);
    }
  });

  const createUserMutation = useMutation({
    mutationFn: async () => {
      const response = await $api.post(`/organizations/${orgId}/users`, {
        email: singleUser.email,
        firstName: singleUser.firstName,
        lastName: singleUser.lastName,
        company: singleUser.company || undefined,
        password: singleUser.password || undefined
      });
      return response.data as { data: { email: string }; message: string };
    },
    onSuccess: (data) => {
      setCreatedUsers((prev) => [data.data, ...prev]);
      setSingleUser(emptySingleUser);
      setSingleUserError(null);
    },
    onError: (e) => {
      setSingleUserError((e as AxiosError<{ message: string }>).response?.data?.message || "Error creating user");
    }
  });

  return (
    <div className="container mx-auto py-8">
      <div className="max-w-2xl mx-auto">
        <FigmaTypography component="h1" variant="memo-heading-1" className="mb-2">Import Users</FigmaTypography>
        <p className="text-[14px] text-gray-500 mb-6">
          Upload a CSV or Excel (.xlsx/.xls) file to bulk-create users in an organization. Columns: <code>email, company, firstName, lastName, password</code>.
          Leave password blank for organizations with the Allow Empty Password feature flag enabled.
        </p>

        <a
          href={"data:text/csv;charset=utf-8," + encodeURIComponent("email,company,firstName,lastName,password\n")}
          download="workshop-users-template.csv"
          className="inline-block mb-6 text-[14px] font-medium text-blue-600 hover:underline"
          data-testid="import-users-template-link"
        >
          Download CSV template
        </a>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="orgId" className="text-[14px] font-medium">
              Organization
            </Label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger id="orgId" className="h-11" data-testid="import-users-org-select">
                <SelectValue placeholder="Select an organization">
                  {selectedOrg ? selectedOrg.name : "Select an organization"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="p-2 border-b border-gray-200">
                  <Input
                    placeholder="Search organizations..."
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    className="h-8 text-[14px]"
                  />
                </div>
                {filteredOrganizations.length === 0 ? (
                  <div className="px-3 py-2 text-[14px] text-gray-500">No organizations found</div>
                ) : (
                  filteredOrganizations.map((org) => (
                    <SelectItem key={org.id} value={org.id.toString()} className={selectItemClasses}>
                      {org.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="file" className="text-[14px] font-medium">
              CSV or Excel File
            </Label>
            <Input
              id="file"
              type="file"
              accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="h-11"
              data-testid="import-users-file-input"
            />
          </div>

          <AppButton
            onClick={() => importMutation.mutate()}
            disabled={!orgId || !file || importMutation.isPending}
            data-testid="import-users-submit"
          >
            {importMutation.isPending ? "Importing..." : "Import"}
          </AppButton>
        </div>

        {result && (
          <div className="mt-8" data-testid="import-users-result">
            <p className="font-medium mb-2">
              Created {result.createdCount} user(s), {result.errorCount} error(s).
            </p>
            {result.errors.length > 0 && (
              <table className="w-full text-[14px] border rounded-md">
                <thead>
                  <tr className="border-b bg-gray-50 text-left">
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((err, index) => (
                    <tr key={index} className="border-b">
                      <td className="px-3 py-2 text-gray-600">{err.row || "—"}</td>
                      <td className="px-3 py-2">{err.email ?? "—"}</td>
                      <td className="px-3 py-2 text-red-600">{err.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="mt-10 border-t pt-8">
          <FigmaTypography component="h2" variant="memo-heading-3" className="mb-1">Add a single user</FigmaTypography>
          <p className="text-[14px] text-gray-500 mb-4">
            Create one user in the selected organization. Leave password blank for organizations with the Allow Empty Password feature flag enabled.
          </p>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="su-firstName" className="text-[14px] font-medium">
                  First name
                </Label>
                <Input
                  id="su-firstName"
                  value={singleUser.firstName}
                  onChange={(e) => setSingleUser((p) => ({ ...p, firstName: e.target.value }))}
                  className="h-11"
                  data-testid="single-user-first-name"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="su-lastName" className="text-[14px] font-medium">
                  Last name
                </Label>
                <Input
                  id="su-lastName"
                  value={singleUser.lastName}
                  onChange={(e) => setSingleUser((p) => ({ ...p, lastName: e.target.value }))}
                  className="h-11"
                  data-testid="single-user-last-name"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="su-email" className="text-[14px] font-medium">
                Email
              </Label>
              <Input
                id="su-email"
                type="email"
                value={singleUser.email}
                onChange={(e) => setSingleUser((p) => ({ ...p, email: e.target.value }))}
                className="h-11"
                data-testid="single-user-email"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="su-company" className="text-[14px] font-medium">
                Company <span className="text-gray-400">(optional)</span>
              </Label>
              <Input
                id="su-company"
                value={singleUser.company}
                onChange={(e) => setSingleUser((p) => ({ ...p, company: e.target.value }))}
                className="h-11"
                data-testid="single-user-company"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="su-password" className="text-[14px] font-medium">
                Password <span className="text-gray-400">(optional)</span>
              </Label>
              <Input
                id="su-password"
                type="text"
                value={singleUser.password}
                onChange={(e) => setSingleUser((p) => ({ ...p, password: e.target.value }))}
                className="h-11"
                data-testid="single-user-password"
              />
            </div>

            {singleUserError && (
              <p className="text-[14px] text-red-600" data-testid="single-user-error">
                {singleUserError}
              </p>
            )}

            <AppButton
              onClick={() => createUserMutation.mutate()}
              disabled={
                !orgId ||
                !singleUser.email ||
                !singleUser.firstName ||
                !singleUser.lastName ||
                createUserMutation.isPending
              }
              data-testid="single-user-submit"
            >
              {createUserMutation.isPending ? "Adding..." : "Add user"}
            </AppButton>
          </div>

          {createdUsers.length > 0 && (
            <div className="mt-6" data-testid="single-user-created-list">
              <p className="font-medium mb-2">Added {createdUsers.length} user(s)</p>
              <ul className="text-[14px] border rounded-md divide-y">
                {createdUsers.map((u, index) => (
                  <li key={index} className="px-3 py-2">
                    {u.email}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
